import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, LockKeyhole, Search } from 'lucide-react';
import { MdKeyboardDoubleArrowRight } from 'react-icons/md';
import { useSelector } from 'react-redux';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RootState } from '@/store';
import { UserRole } from '@/types';
import { cn, formatNumberWithCommas, useRoleIds } from '@/utils';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { useDebounce, useOutsideClick } from '@/hooks';
import { useLazyGetMatchesQuery } from '@/store/api/sportsApi';

interface MenuLink {
  name: string;
  href: string;
}

const REPORT_LINKS: MenuLink[] = [
  { name: 'Account Statement', href: '/rolewise-account-statement' },
  { name: 'Profit Loss', href: '/pl-by-event?event=Sports' },
  { name: 'Chip Statement', href: '/reports/chip-statement' },
  { name: 'Chip Summary', href: '/reports/chip-summary' },
  { name: 'Settlement Report', href: '/pl-by-player' },
  { name: 'Sport Summary Report', href: '/reports/sports-summary' },
  { name: 'Top Clients', href: '/reports/top-clients' },
  { name: 'Settlement', href: '/reports/settlement' },
  // { name: 'Balance Sheet', href: '/reports/balance-sheet' },
];

const DOWNLINE_ROLE_VISIBILITY: Record<UserRole, UserRole[]> = {
  OWNER: ['SUPER MASTER', 'MASTER', 'AGENT', 'USER'],
  'SUPER MASTER': ['MASTER', 'AGENT', 'USER'],
  MASTER: ['USER', 'AGENT'],
  AGENT: ['USER'],
  USER: [],
  DEMO: [],
  'RESULT MANAGER': [],
};

const useUserLinks = (): MenuLink[] => {
  const user = useSelector((state: RootState) => state.user);
  const { isRoleUser } = useRoleCheck();
  const { superMasterId, masterId, agentId, userId } = useRoleIds();
  const userRole = user?.data?.role?.name as UserRole | undefined;
  const effectiveRole = isRoleUser ? 'OWNER' : userRole;
  const visibleRoles =
    effectiveRole && effectiveRole in DOWNLINE_ROLE_VISIBILITY
      ? DOWNLINE_ROLE_VISIBILITY[effectiveRole]
      : [];

  return [
    { name: 'Super Master', href: `/members/${superMasterId}`, role: 'SUPER MASTER' },
    { name: 'Master', href: `/members/${masterId}`, role: 'MASTER' },
    { name: 'Agent', href: `/members/${agentId}`, role: 'AGENT' },
    { name: 'User', href: `/members/${userId}`, role: 'USER' },
    { name: 'Blocked User', href: '/inactive-members', role: 'USER' },
  ].filter((item) => visibleRoles.includes(item.role as UserRole));
};

const NavDropdown = ({ label, links }: { label: string; links: MenuLink[] }) => {
  const location = useLocation();
  const isActive = (href: string) => location.pathname + location.search === href;

  if (links.length === 0) return null;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="flex items-center gap-1.5 text-sm text-gray-600 outline-none hover:text-[#F08821] data-[state=open]:text-[#F08821]">
        {label}
        <ChevronDown className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[240px] rounded-none bg-white p-0 py-1">
        {links.map((link) => (
          <DropdownMenuItem key={link.href} asChild className="cursor-pointer rounded-none p-0">
            <NavLink
              to={link.href}
              className={cn(
                'flex items-center gap-1 px-5 py-1.5 text-sm text-gray-800 hover:text-[#F08821] focus:bg-transparent',
                isActive(link.href) && 'text-[#F08821]'
              )}
            >
              <MdKeyboardDoubleArrowRight className="h-3.5 w-3.5" />
              {link.name}
            </NavLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const EventSearch = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debouncedSearch = useDebounce(search.trim(), 400);
  const [triggerGetMatches, { data, isFetching }] = useLazyGetMatchesQuery();
  const containerRef = useOutsideClick<HTMLDivElement>(() => setIsOpen(false));

  useEffect(() => {
    if (!debouncedSearch) {
      setIsOpen(false);
      return;
    }
    triggerGetMatches({ search: debouncedSearch });
    setIsOpen(true);
  }, [debouncedSearch, triggerGetMatches]);

  const results = data?.fixture ?? [];

  const handleSelect = (eventId: number) => {
    navigate(`/sport-analysis/event/${eventId}`);
    setSearch('');
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative ml-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => debouncedSearch && setIsOpen(true)}
          placeholder="Search Events"
          className="h-8 w-40 rounded border border-gray-300 pl-8 pr-3 text-xs outline-none focus:border-[#F08821] sm:w-48"
        />
      </div>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full min-w-[260px] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
          {isFetching ? (
            <div className="px-4 py-3 text-xs text-gray-500">Searching...</div>
          ) : results.length > 0 ? (
            <ul className="max-h-72 overflow-y-auto py-1">
              {results.map((fixture) => (
                <li key={fixture.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(fixture.id)}
                    className="group flex w-full flex-col items-start gap-0.5 px-4 py-2 text-left text-xs text-gray-800 hover:bg-[#F08821] hover:text-white"
                  >
                    <span className="w-full truncate font-medium">{fixture.name}</span>
                    {fixture.competition?.name && (
                      <span className="w-full truncate text-[10px] text-gray-400 group-hover:text-white">
                        {fixture.competition.name}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-3 text-center text-xs text-gray-500">No events found</div>
          )}
        </div>
      )}
    </div>
  );
};

interface HeaderNavProps {
  mainAmount: string | number;
  onChangePassword: () => void;
  onLogout: () => void;
}

export function HeaderNav({ mainAmount, onChangePassword, onLogout }: HeaderNavProps) {
  const user = useSelector((state: RootState) => state.user);
  const userLinks = useUserLinks();
  const { isOwner } = useRoleCheck();
  const displayName = user?.data?.username || user?.data?.firstname || 'Owner';

  return (
    <>
      {isOwner && <EventSearch />}

      <div className="ml-auto flex items-center gap-4 sm:gap-7">
        <div className="hidden items-center gap-7 md:flex">
          <NavDropdown label="Reports" links={REPORT_LINKS} />
          <NavDropdown label="Users" links={userLinks} />
        </div>

        <DropdownMenu modal={false}>
          <DropdownMenuTrigger className="flex items-center gap-1 outline-none">
            <span className="text-sm font-semibold text-black">{displayName}</span>
            <img src="/images/profile.png" alt="" className="h-10 w-10 rounded-full shadow-md" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[190px] rounded-none bg-white p-0">
            <div className="bg-green-100 px-5 py-2 text-sm font-semibold text-green-700">
              Balance: {formatNumberWithCommas(mainAmount)}
            </div>
            <DropdownMenuItem
              onSelect={onChangePassword}
              className="cursor-pointer gap-1.5 rounded-none px-5 py-1.5 text-sm text-gray-800 hover:text-[#F08821]"
            >
              <LockKeyhole className="h-4 w-4" />
              Change password
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={onLogout}
              className="cursor-pointer gap-1.5 rounded-none px-5 py-1.5 text-sm text-gray-800 hover:text-[#F08821]"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </DropdownMenuItem>
            <DropdownMenuSeparator className="m-0" />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
